import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  createStyles,
  Divider,
  makeStyles,
  Typography,
} from "@material-ui/core";

import { fetchCustomer } from "../../helper/Api";
import ErrorDialog from "../common/ErrorDialog";
import { errors, getApiError } from "../../helper/Error";
import PhoneIcon from "@material-ui/icons/PhoneInTalk";
import PinIcon from "@material-ui/icons/Room";

// CSSスタイルを定義
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      margin: theme.spacing(2),
    },
    card: {
      padding: theme.spacing(2),
      whiteSpace: "pre-line", // \nで改行用
      width: "100%",
      maxWidth: "600px",
    },
    title: {
      textAlign: "center",
      marginBottom: theme.spacing(1),
    },
    category: {
      lineHeight: 1,
    },
    contents: {
      marginBottom: theme.spacing(2),
    },
    loader: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(22),
    },
    nothing: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      whiteSpace: "pre-line",
      fontSize: 22,
      marginTop: theme.spacing(22),
    },
    button: {
      paddingTop: 0,
      paddingBottom: 0,
      textAlign: "left",
    },
    decoration: {
      textDecoration: "none",
    },
  })
);

export default function Customer() {
  const location = useLocation();
  const { serviceId, position } = location.state;
  const classes = useStyles();

  const categories = [
    "住所",
    "電話番号",
    "携帯番号",
    "要介護区分",
    "障害支援区分",
    "担当ケアマネ",
    "事業所",
  ];

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const errorRef = useRef({});
  const [isOpenError, setIsOpenError] = useState(false);
  const handleCloseError = useCallback(() => {
    setIsOpenError(false);
  }, []);

  useEffect(() => {
    const fn = async () => {
      await fetchCustomer(serviceId)
        .then((res) => {
          // 取得データが利用者名のみの場合、表示許可なしと判断
          const isNotAllow = Object.keys(res.data.result).length === 1;

          if (isNotAllow) {
            return;
          }

          if (res.data.error.length === 0) setData(res.data.result);
          else throw res.data.error;
        })
        .catch((err) => {
          errorRef.current = err.code
            ? getApiError(err)
            : errors.APP_CONNECTION_FAILED;
          setIsOpenError(true);
        });
      setIsLoading(false);
    };
    fn();
  }, [serviceId]);

  const getContent = useCallback(
    (category) => {
      switch (category) {
        case "住所":
          return data.address;
        case "電話番号":
          return data.tel;
        case "携帯番号":
          return data.mbtel;
        case "要介護区分":
          return data.kaigo && data.kaigo.caretype;
        case "障害支援区分":
          return data.shougai && data.shougai.caretype;
        case "担当ケアマネ":
          return data.kaigo && data.kaigo.caremanager;
        case "事業所":
          return data.kaigo && data.kaigo.kyotakujigyousho;
        default:
          return undefined;
      }
    },
    [data]
  );

  const isAllowDisplay = (category) => getContent(category) !== undefined;
  const isUnsetContent = (category) => getContent(category) === null;

  const isIOS = () =>  {
    const navigator = window.navigator;
    // iOSのユーザーエージェントかつタッチデバイスであることを確認
    return /iPhone|iPad|iPod/.test(navigator.userAgent) && navigator.maxTouchPoints >= 1;
  }

  const isIPadOS = () => {
    const navigator = window.navigator;
    // iPadOSのユーザーエージェントかつタッチデバイスであることを確認
    return /iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints >= 1;
  }

  const getMapUrl = (address) => {
    const destination = position ?? address;

    if (isIOS() || isIPadOS()) {
      return `http://maps.apple.com/?daddr=${destination}`;
    }

    return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
  }

  return (
    <>
      {isLoading ? (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      ) : data.length === 0 ? (
        <Typography color="textSecondary" className={classes.nothing}>
          {"利用者情報はありません"}
        </Typography>
      ) : (
        <div className={classes.root}>
          <Card elevation={3} className={classes.card}>
            <Typography variant="subtitle1" className={classes.title}>
              {data.riyousha}
            </Typography>
            <Divider />
            <CardContent>
              {categories.map(
                (category, index) =>
                  isAllowDisplay(category) && (
                    <div key={index}>
                      <Typography
                        variant="overline"
                        color="textSecondary"
                        className={classes.category}
                      >
                        {category}
                      </Typography>

                      {isUnsetContent(category) ? (
                        <Typography
                          variant="subtitle2"
                          className={classes.contents}
                          color="textSecondary"
                        >
                          未登録
                        </Typography>
                      ) : category === "電話番号" ||
                        category === "携帯番号" ||
                        category === "住所" ? (
                        <div className={classes.contents}>
                          <a
                            href={
                              category === "電話番号" || category === "携帯番号"
                                ? `tel:${getContent(category)}`
                                : getMapUrl(getContent(category))
                            }
                            className={classes.decoration}
                            rel={
                              category === "住所" ? "noopener noreferrer" : null
                            }
                            target={category === "住所" ? "_blank" : null}
                          >
                            <Button
                              className={classes.button}
                              startIcon={
                                category === "電話番号" ||
                                category === "携帯番号" ? (
                                  <PhoneIcon color="action" />
                                ) : (
                                  <PinIcon color="action" />
                                )
                              }
                              color="primary"
                            >
                              {getContent(category)}
                            </Button>
                          </a>
                        </div>
                      ) : (
                        <Typography
                          variant="subtitle2"
                          className={classes.contents}
                          color="textPrimary"
                        >
                          {getContent(category)}
                        </Typography>
                      )}
                    </div>
                  )
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <ErrorDialog
        open={isOpenError}
        onClose={handleCloseError}
        error={errorRef.current}
      />
    </>
  );
}
